export type PortalLanguage = 'ar' | 'en';

const localeFor = (language: PortalLanguage) => (language === 'ar' ? 'ar-EG' : 'en-US');

export const formatEgp = (value: number, language: PortalLanguage): string =>
  new Intl.NumberFormat(localeFor(language), {
    style: 'currency',
    currency: 'EGP',
    maximumFractionDigits: 2,
  }).format(value);

// The database sends "dd-MM-yyyy  h:mmAM" (SQL styles 105 + 100), which `new Date` cannot read.
const SQL_VISIT_DATE = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?)?$/;

/** Returns the visit date, or null when the text is neither the database format nor something `Date` can read. */
export const parseVisitDate = (raw: string): Date | null => {
  const text = raw.trim();
  const match = SQL_VISIT_DATE.exec(text);
  if (match) {
    const [, day, month, year, hourText, minute, meridiem] = match;
    let hour = hourText ? Number(hourText) : 0;
    if (meridiem) {
      const isPm = meridiem.toLowerCase() === 'pm';
      hour = (hour % 12) + (isPm ? 12 : 0);
    }
    const date = new Date(Number(year), Number(month) - 1, Number(day), hour, minute ? Number(minute) : 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const fallback = new Date(text);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

export const formatVisitDate = (raw: string, language: PortalLanguage): string => {
  const date = parseVisitDate(raw);
  if (!date) return raw;

  return new Intl.DateTimeFormat(localeFor(language), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

export const interpolate = (template: string, vars: Record<string, string | number> = {}): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );

interface TestNode {
  children: TestNode[];
}

export const countTests = (tests: TestNode[]): number =>
  tests.reduce((total, test) => total + 1 + countTests(test.children), 0);
